import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Conversation,
  CreateConversationParams,
  ConversationFilters,
  MemberInfo,
  UpdateMemberInfoParams,
  ConversationValidationError,
} from '../types/message';
import { ChangeTrackingService } from './changeTrackingService';
import { NotificationService } from './notificationService';

/**
 * ConversationService - Manages conversations (direct and group chats)
 *
 * Features:
 * - Create and manage direct (1:1) conversations
 * - Create and manage group conversations (3-50 members)
 * - Member management (add, remove, update settings)
 * - Conversation metadata (name, description, avatar)
 * - Per-member settings (mute, archive, notifications)
 * - Read/unread tracking
 * - Pinned messages (max 3)
 *
 * Integration:
 * - Logs all changes via ChangeTrackingService
 * - Sends notifications via NotificationService
 * - Enforces permissions and validation rules
 */
class ConversationServiceImpl {
  private readonly conversationsCollection = 'conversations';

  /**
   * Create a new conversation (direct or group)
   */
  async createConversation(
    params: CreateConversationParams,
    createdBy: string
  ): Promise<string> {
    try {
      // 1. Validate params
      this.validateCreateConversation(params, createdBy);

      // 2. Check for existing direct conversation
      if (params.type === 'direct') {
        const existing = await this.findExistingDirectConversation(
          params.participants
        );
        if (existing) {
          return existing.id; // Return existing conversation
        }
      }

      // 3. Create conversation document
      const conversationId = doc(collection(db, this.conversationsCollection)).id;
      const now = new Date();

      // Initialize member info for all participants
      const memberInfo: Record<string, MemberInfo> = {};
      params.participants.forEach(userId => {
        memberInfo[userId] = {
          joined: now,
          addedBy: createdBy,
          unreadCount: 0,
          muted: false,
          notifications: params.type === 'direct' ? 'all' : 'mentions',
          archived: false,
        };
      });

      // Build conversation object without undefined fields
      const conversation: Conversation = {
        id: conversationId,
        type: params.type,
        participants: params.participants,
        memberInfo,
        lastActivity: now,
        createdAt: now,
        updatedAt: now,
      } as Conversation;

      // Add group-specific fields
      if (params.type === 'group') {
        if (params.name) {
          conversation.name = params.name;
        }
        if (params.description) {
          conversation.description = params.description;
        }
        conversation.creator = createdBy;
        conversation.admins = [createdBy];
        conversation.settings = {
          anyoneCanAdd: params.settings?.anyoneCanAdd ?? false,
          anyoneCanChangeName: params.settings?.anyoneCanChangeName ?? false,
          anyoneCanChangeAvatar: params.settings?.anyoneCanChangeAvatar ?? false,
        };
      } else {
        // Direct messages may have optional name/description
        if (params.name) {
          conversation.name = params.name;
        }
        if (params.description) {
          conversation.description = params.description;
        }
      }

      // 4. Save to Firestore
      const firestoreData = this.toFirestore(conversation);

      await setDoc(
        doc(db, this.conversationsCollection, conversationId),
        firestoreData
      );

      // 5. Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'conversation' as any,
          entityId: conversationId,
          changeType: 'create',
          beforeState: null,
          afterState: conversation,
          changedFields: Object.keys(conversation),
        },
        createdBy,
        'User', // userName - would be fetched from user service in production
        false // isAdmin - assume non-admin, admins auto-approved
      );

      // 6. Send notifications to other participants
      if (params.type === 'group') {
        const otherParticipants = params.participants.filter(id => id !== createdBy);
        await NotificationService.createBulkNotification(otherParticipants, {
          type: 'group_chat_added',
          title: `Added to "${params.name}"`,
          message: `You've been added to a new group chat.`,
          actionUrl: `/messages/${conversationId}`,
          actionLabel: 'View Group',
          data: { conversationId, groupName: params.name },
        });
      }

      return conversationId;
    } catch (error: any) {
      console.error('Error creating conversation:', error);
      throw new Error(`Failed to create conversation: ${error.message}`);
    }
  }

  /**
   * Get conversation by ID
   */
  async getConversationById(conversationId: string): Promise<Conversation | null> {
    try {
      const docRef = doc(db, this.conversationsCollection, conversationId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.fromFirestore(docSnap.id, docSnap.data());
    } catch (error: any) {
      console.error('Error getting conversation:', error);
      throw new Error(`Failed to get conversation: ${error.message}`);
    }
  }

  /**
   * Get all conversations for a user
   */
  async getUserConversations(
    userId: string,
    filters?: ConversationFilters
  ): Promise<Conversation[]> {
    try {
      let q = query(
        collection(db, this.conversationsCollection),
        where('participants', 'array-contains', userId),
        orderBy('lastActivity', 'desc')
      );

      if (filters?.type) {
        q = query(q, where('type', '==', filters.type));
      }

      if (filters?.limit) {
        q = query(q, firestoreLimit(filters.limit));
      }

      const snapshot = await getDocs(q);
      let conversations = snapshot.docs.map(doc =>
        this.fromFirestore(doc.id, doc.data())
      );

      // Client-side filtering for archived status
      if (!filters?.includeArchived) {
        conversations = conversations.filter(
          conv => !conv.memberInfo[userId]?.archived
        );
      }

      // Client-side filtering for unread
      if (filters?.onlyUnread) {
        conversations = conversations.filter(
          conv => (conv.memberInfo[userId]?.unreadCount ?? 0) > 0
        );
      }

      return conversations;
    } catch (error: any) {
      console.error('Error getting user conversations:', error);
      throw new Error(`Failed to get conversations: ${error.message}`);
    }
  }

  /**
   * Update conversation metadata (name, description, avatar)
   */
  async updateConversation(
    conversationId: string,
    updates: Partial<Pick<Conversation, 'name' | 'description' | 'avatar' | 'settings'>>,
    userId: string
  ): Promise<void> {
    try {
      // 1. Get current conversation
      const conversation = await this.getConversationById(conversationId);
      if (!conversation) {
        throw new ConversationValidationError('Conversation not found');
      }

      // 2. Check permissions
      if (conversation.type === 'group') {
        const isAdmin = conversation.admins?.includes(userId);
        const canChangeName = conversation.settings?.anyoneCanChangeName || isAdmin;
        const canChangeAvatar = conversation.settings?.anyoneCanChangeAvatar || isAdmin;

        if (updates.name && !canChangeName) {
          throw new ConversationValidationError('Only admins can change group name');
        }
        if (updates.avatar && !canChangeAvatar) {
          throw new ConversationValidationError('Only admins can change group avatar');
        }
      } else {
        throw new ConversationValidationError('Cannot update direct conversations');
      }

      // 3. Update Firestore
      const docRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });

      // 4. Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'conversation' as any,
          entityId: conversationId,
          changeType: 'update',
          beforeState: conversation,
          afterState: { ...conversation, ...updates },
          changedFields: Object.keys(updates),
        },
        userId,
        'User',
        false
      );
    } catch (error: any) {
      console.error('Error updating conversation:', error);
      throw error;
    }
  }

  /**
   * Add members to a group conversation
   */
  async addMembers(
    conversationId: string,
    userIds: string[],
    addedBy: string
  ): Promise<void> {
    try {
      // 1. Get conversation
      const conversation = await this.getConversationById(conversationId);
      if (!conversation) {
        throw new ConversationValidationError('Conversation not found');
      }
      if (conversation.type !== 'group') {
        throw new ConversationValidationError('Can only add members to groups');
      }

      // 2. Check permissions
      const isAdmin = conversation.admins?.includes(addedBy);
      const canAdd = conversation.settings?.anyoneCanAdd || isAdmin;
      if (!canAdd) {
        throw new ConversationValidationError('Only admins can add members');
      }

      // 3. Check group size limit
      if (conversation.participants.length + userIds.length > 50) {
        throw new ConversationValidationError('Group size limit (50) exceeded');
      }

      // 4. Update Firestore
      const docRef = doc(db, this.conversationsCollection, conversationId);
      const now = new Date();

      const newMemberInfo: Record<string, any> = {};
      userIds.forEach(userId => {
        newMemberInfo[`memberInfo.${userId}`] = {
          joined: Timestamp.fromDate(now),
          addedBy,
          unreadCount: 0,
          muted: false,
          notifications: 'mentions',
          archived: false,
        };
      });

      await updateDoc(docRef, {
        participants: arrayUnion(...userIds),
        ...newMemberInfo,
        updatedAt: Timestamp.now(),
      });

      // 5. Send notifications
      await NotificationService.createBulkNotification(userIds, {
        type: 'group_chat_added',
        title: `Added to "${conversation.name}"`,
        message: `You've been added to a group chat.`,
        actionUrl: `/messages/${conversationId}`,
        actionLabel: 'View Group',
      });

      // 6. Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'conversation' as any,
          entityId: conversationId,
          changeType: 'update',
          afterState: { addedMembers: userIds },
          changedFields: ['participants', 'memberInfo'],
        },
        addedBy,
        'User',
        false
      );
    } catch (error: any) {
      console.error('Error adding members:', error);
      throw error;
    }
  }

  /**
   * Remove member from group conversation
   */
  async removeMember(
    conversationId: string,
    userIdToRemove: string,
    removedBy: string
  ): Promise<void> {
    try {
      const conversation = await this.getConversationById(conversationId);
      if (!conversation) {
        throw new ConversationValidationError('Conversation not found');
      }
      if (conversation.type !== 'group') {
        throw new ConversationValidationError('Can only remove from groups');
      }

      // Check permissions (admins can remove, or users can remove themselves)
      const isAdmin = conversation.admins?.includes(removedBy);
      const isSelf = userIdToRemove === removedBy;
      if (!isAdmin && !isSelf) {
        throw new ConversationValidationError('Only admins can remove other members');
      }

      // Update Firestore
      const docRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(docRef, {
        participants: arrayRemove(userIdToRemove),
        [`memberInfo.${userIdToRemove}`]: null, // Remove member info
        admins: arrayRemove(userIdToRemove), // Remove from admins if applicable
        updatedAt: Timestamp.now(),
      });

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'conversation' as any,
          entityId: conversationId,
          changeType: 'update',
          afterState: { removedMember: userIdToRemove },
          changedFields: ['participants', 'memberInfo'],
        },
        removedBy,
        'User',
        false
      );
    } catch (error: any) {
      console.error('Error removing member:', error);
      throw error;
    }
  }

  /**
   * Update member-specific settings (mute, archive, notifications)
   */
  async updateMemberInfo(
    conversationId: string,
    userId: string,
    updates: UpdateMemberInfoParams
  ): Promise<void> {
    try {
      const docRef = doc(db, this.conversationsCollection, conversationId);
      const updateData: any = {};

      if (updates.nickname !== undefined) {
        updateData[`memberInfo.${userId}.nickname`] = updates.nickname;
      }
      if (updates.muted !== undefined) {
        updateData[`memberInfo.${userId}.muted`] = updates.muted;
      }
      if (updates.notifications !== undefined) {
        updateData[`memberInfo.${userId}.notifications`] = updates.notifications;
      }
      if (updates.archived !== undefined) {
        updateData[`memberInfo.${userId}.archived`] = updates.archived;
      }

      await updateDoc(docRef, updateData);
    } catch (error: any) {
      console.error('Error updating member info:', error);
      throw new Error(`Failed to update member info: ${error.message}`);
    }
  }

  /**
   * Mark conversation as read (reset unread count)
   */
  async markAsRead(conversationId: string, userId: string): Promise<void> {
    try {
      const docRef = doc(db, this.conversationsCollection, conversationId);
      await updateDoc(docRef, {
        [`memberInfo.${userId}.unreadCount`]: 0,
        [`memberInfo.${userId}.lastRead`]: Timestamp.now(),
      });
    } catch (error: any) {
      console.error('Error marking as read:', error);
      throw new Error(`Failed to mark as read: ${error.message}`);
    }
  }

  /**
   * Increment unread count for other participants
   */
  async incrementUnreadCount(conversationId: string, excludeUserId: string): Promise<void> {
    try {
      const conversation = await this.getConversationById(conversationId);
      if (!conversation) return;

      const docRef = doc(db, this.conversationsCollection, conversationId);
      const updates: any = {};

      conversation.participants
        .filter(id => id !== excludeUserId)
        .forEach(userId => {
          const currentCount = conversation.memberInfo[userId]?.unreadCount ?? 0;
          updates[`memberInfo.${userId}.unreadCount`] = currentCount + 1;
        });

      await updateDoc(docRef, updates);
    } catch (error: any) {
      console.error('Error incrementing unread count:', error);
    }
  }

  /**
   * Pin/unpin message
   */
  async togglePinnedMessage(
    conversationId: string,
    messageId: string,
    userId: string
  ): Promise<void> {
    try {
      const conversation = await this.getConversationById(conversationId);
      if (!conversation) {
        throw new ConversationValidationError('Conversation not found');
      }

      // Check if admin for groups
      if (conversation.type === 'group' && !conversation.admins?.includes(userId)) {
        throw new ConversationValidationError('Only admins can pin messages');
      }

      const currentPinned = conversation.pinnedMessages ?? [];
      const isPinned = currentPinned.includes(messageId);

      const docRef = doc(db, this.conversationsCollection, conversationId);

      if (isPinned) {
        // Unpin
        await updateDoc(docRef, {
          pinnedMessages: arrayRemove(messageId),
        });
      } else {
        // Pin (limit 3)
        if (currentPinned.length >= 3) {
          throw new ConversationValidationError('Maximum 3 pinned messages allowed');
        }
        await updateDoc(docRef, {
          pinnedMessages: arrayUnion(messageId),
        });
      }
    } catch (error: any) {
      console.error('Error toggling pinned message:', error);
      throw error;
    }
  }

  // ============ PRIVATE HELPER METHODS ============

  private validateCreateConversation(
    params: CreateConversationParams,
    createdBy: string
  ): void {
    if (!params.participants || params.participants.length < 2) {
      throw new ConversationValidationError('At least 2 participants required');
    }

    if (!params.participants.includes(createdBy)) {
      throw new ConversationValidationError('Creator must be in participants');
    }

    if (params.type === 'direct' && params.participants.length !== 2) {
      throw new ConversationValidationError('Direct conversations must have exactly 2 participants');
    }

    if (params.type === 'group') {
      if (params.participants.length < 3) {
        throw new ConversationValidationError('Group conversations must have at least 3 participants');
      }
      if (params.participants.length > 50) {
        throw new ConversationValidationError('Group conversations cannot exceed 50 participants');
      }
      if (!params.name) {
        throw new ConversationValidationError('Group conversations must have a name');
      }
    }
  }

  private async findExistingDirectConversation(
    participants: string[]
  ): Promise<Conversation | null> {
    try {
      // Validate participants array
      if (!participants || participants.length !== 2) {
        console.warn('Invalid participants array for direct conversation:', participants);
        return null;
      }

      // Filter out any undefined/null values
      const validParticipants = participants.filter(p => p != null && p !== '');
      if (validParticipants.length !== 2) {
        console.warn('Participants contains undefined/null values:', participants);
        return null;
      }

      const [user1, user2] = validParticipants.sort(); // Sort for consistent querying
      const q = query(
        collection(db, this.conversationsCollection),
        where('type', '==', 'direct'),
        where('participants', '==', [user1, user2])
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      return this.fromFirestore(snapshot.docs[0].id, snapshot.docs[0].data());
    } catch (error) {
      console.error('Error finding existing conversation:', error);
      return null;
    }
  }

  /**
   * Convert Firestore timestamps to Date objects
   */
  private fromFirestore(id: string, data: any): Conversation {
    return {
      ...data,
      id,
      lastActivity: data.lastActivity?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      memberInfo: Object.entries(data.memberInfo || {}).reduce((acc, [userId, info]: [string, any]) => {
        acc[userId] = {
          ...info,
          joined: info.joined?.toDate(),
          lastRead: info.lastRead?.toDate(),
        };
        return acc;
      }, {} as Record<string, MemberInfo>),
      lastMessage: data.lastMessage ? {
        ...data.lastMessage,
        timestamp: data.lastMessage.timestamp?.toDate(),
      } : undefined,
    } as Conversation;
  }

  /**
   * Convert Date objects to Firestore timestamps
   * Also removes undefined fields to prevent Firestore errors
   */
  private toFirestore(conversation: Conversation): any {
    const data: any = {
      id: conversation.id,
      type: conversation.type,
      participants: conversation.participants,
      memberInfo: Object.entries(conversation.memberInfo).reduce((acc, [userId, info]) => {
        acc[userId] = {
          unreadCount: info.unreadCount,
          muted: info.muted,
          notifications: info.notifications,
          archived: info.archived,
          addedBy: info.addedBy,
          joined: Timestamp.fromDate(info.joined),
          ...(info.nickname && { nickname: info.nickname }),
          ...(info.lastRead && { lastRead: Timestamp.fromDate(info.lastRead) }),
        };
        return acc;
      }, {} as Record<string, any>),
      lastActivity: Timestamp.fromDate(conversation.lastActivity),
      createdAt: Timestamp.fromDate(conversation.createdAt),
      updatedAt: Timestamp.fromDate(conversation.updatedAt),
    };

    // Only include optional fields if they exist
    if (conversation.name) {
      data.name = conversation.name;
    }
    if (conversation.description) {
      data.description = conversation.description;
    }
    if (conversation.avatar) {
      data.avatar = conversation.avatar;
    }
    if (conversation.creator) {
      data.creator = conversation.creator;
    }
    if (conversation.admins && conversation.admins.length > 0) {
      data.admins = conversation.admins;
    }
    if (conversation.settings) {
      data.settings = conversation.settings;
    }
    if (conversation.lastMessage) {
      data.lastMessage = {
        text: conversation.lastMessage.text,
        senderId: conversation.lastMessage.senderId,
        type: conversation.lastMessage.type,
        timestamp: Timestamp.fromDate(conversation.lastMessage.timestamp),
      };
    }
    if (conversation.pinnedMessages && conversation.pinnedMessages.length > 0) {
      data.pinnedMessages = conversation.pinnedMessages;
    }

    return data;
  }
}

export const ConversationService = new ConversationServiceImpl();
